<script lang="ts">
  import { changeQueryParam } from '$lib/navigation';
  import { setupReload } from '$lib/api';
  import LottieSurprised from '$lib/components/lottie/LottieSurprised.svelte';
  import ProductGrid from '$lib/components/ProductGrid.svelte';
  import { afterNavigate, beforeNavigate, invalidate } from '$app/navigation';
  import type { PageData } from './$types';
  import { _ITEMS_RENDERED, _currentLiveAt } from './+page';
  import ProfileHeadMeta from '$lib/components/ProfileHeadMeta.svelte';
  import { getProducts } from '$lib/product';
  import { browser } from '$app/environment';

  export let data: PageData;

  $: ({ profile, products, currentPage, dependencies, baseUrl, isTestMode, testAppId } = data);

  $: hasNext = products?.length === _ITEMS_RENDERED;

  setupReload(() => dependencies, {
    shouldReloadWithBackoff: () => !products,
    shouldPoll: () => true,
  });

  let pollTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Polling function for upcoming products. Checks to see if there are any
   * products coming up once per minute. If so, loads all of them and finds the
   * next upcoming product, then schedules a reload when that becomes live.
   */
  const pollUpcoming = async () => {
    let upcomingProductLiveAt: string | null = null;

    try {
      let offset = 0;
      let hasUpcomingProducts = true;

      const liveAt = new Date();

      // Add 2 minutes for the preview window
      liveAt.setMinutes(liveAt.getMinutes() + 2);

      // Round down to the nearest 5 second interval so we are more likely to hit the cache
      const fiveSecs = 1000 * 5;
      liveAt.setTime(Math.floor(liveAt.getTime() / fiveSecs) * fiveSecs);

      while (hasUpcomingProducts) {
        // Fetch a limited number of items at a time. We don't know how many items
        // might appear at once in the upcoming timeframe, so we may need load
        // more that one set until we find the next upcoming item.

        const products = await getProducts(fetch, {
          baseUrl,
          offset,
          limit: _ITEMS_RENDERED,
          liveAt,
          isTestMode,
        });

        // If no more products, break the loop. The upcoming product will have
        // been set by the last fetch iteration.
        if (!products || products.length === 0) {
          hasUpcomingProducts = false;
          break;
        }

        for (let p of products) {
          if ($_currentLiveAt < new Date(p.liveAt)) {
            // If the product is live in the future, set the date but continue. We
            // continue inside the loop until the _last_ product which is live in
            // the future.
            upcomingProductLiveAt = p.liveAt;
          } else {
            // If the product is _not_ live in the future, then we've found the
            // first currently live product. Set `hasUpcomingProducts` to false to
            // break the outer loop, and break the inner loop.
            hasUpcomingProducts = false;
            break;
          }
        }

        offset += _ITEMS_RENDERED;
      }
    } finally {
      // Stop polling if we've navigated already
      if (currentPage === 1) {
        if (upcomingProductLiveAt) {
          // If we found an upcoming live at date, set a timeout for when that time
          // passes, update the _currentLiveAt store and invalidate deps to force a
          // reload.
          const liveAtDate = new Date(upcomingProductLiveAt);
          const msUntilProductLiveAt = liveAtDate.getTime() - new Date().getTime();

          pollTimeout = setTimeout(() => {
            _currentLiveAt.set(liveAtDate);
            dependencies.map(invalidate);
            pollUpcoming();
          }, msUntilProductLiveAt);
        } else {
          // Otherwise, poll again in a minute
          pollTimeout = setTimeout(pollUpcoming, 1000 * 30);
        }
      }
    }
  };

  afterNavigate(() => {
    // After navigating to the page, update the _currentLiveAt store so we load
    // the most recent items (ratchet forward)
    _currentLiveAt.set(new Date());

    if (currentPage === 1 && !pollTimeout) {
      // If we're on page 1 and we're not already polling somehow, start polling
      pollUpcoming();
    }
  });

  beforeNavigate(() => {
    // Clear the poll timeout if it exists
    if (pollTimeout) {
      clearTimeout(pollTimeout);
      pollTimeout = null;
    }
  });

  async function changePage({ detail: page }: { detail: number }) {
    if (browser) {
      await changeQueryParam('page', page);
      window.scrollTo(0, 0);
    }
  }
</script>

<svelte:head>
  {#if profile}
    <title>@{profile.username} | Bitski</title>

    <ProfileHeadMeta {profile} />
  {:else}
    <title>Bitski</title>
  {/if}
</svelte:head>

<ProductGrid {products} {currentPage} {hasNext} {testAppId} on:change={changePage}>
  <div slot="empty">
    <LottieSurprised className="w-6 h-6 mb-2" />

    <div class="mb-4">
      {#if currentPage > 1}
        No more results
      {:else}
        Nothing here yet
      {/if}
    </div>
  </div>
</ProductGrid>
